import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Input, Button, Switch, Select, Slider } from 'antd';
import ModalDlg from 'components/Basic/ModalDlg';
import { Close, ArrowForward, ArrowDropDown } from '@material-ui/icons';
import { Img } from 'react-image';
import toast from 'components/Basic/Toast';
import { useCreatePrizeDrawMutation } from 'core/worldCup/prizeDraws/PrizeDrawsService';
import { useLazyGetSetupListQuery } from 'core/worldCup/setup/SetupService';
import { Setup } from 'core/worldCup/setup/types';
import RouletteWheel, { RouletteSegment, RouletteWheelRef } from './RouletteWheel';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { VALID_IMAGE_FORMATS } from 'utilities/constants';
import { validateImageBytes } from 'utilities/validateImageFile';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';
dayjs.extend(utc);

type ConfigFormData = {
  prizeConfigId: number;
  alternatesCount: number;
  includePreviousWinners: boolean;
};

const DEMO_SEGMENTS: RouletteSegment[] = Array.from({ length: 16 }, (_, i) => ({
  id: i + 1,
  label: `${i + 1}`,
}));

type PrizeDrawsConfigModalProps = {
  mode: 'modal' | 'fullscreen';
  isOpenModal?: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  campaignId?: number | null;
};

const PrizeDrawsConfigModal: React.FC<PrizeDrawsConfigModalProps> = ({
  mode,
  isOpenModal,
  onClose,
  onSaveSuccess,
  campaignId,
}) => {
  const [form] = Form.useForm<ConfigFormData>();
  const intl = useIntl();
  const fileRef = useRef<HTMLInputElement>(null);
  const isModal = mode === 'modal';

  const [fileSrc, setFileSrc] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [configurations, setConfigurations] = useState<Setup[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<Setup | null>(null);

  const wheelRef = useRef<RouletteWheelRef>(null);
  const [spinPhase, setSpinPhase] = useState<'idle' | 'spinning' | 'finished'>('idle');
  const [spinSpeed, setSpinSpeed] = useState(800);
  const [decelDuration, setDecelDuration] = useState(5000);
  const [selectedSegment, setSelectedSegment] = useState<RouletteSegment | null>(null);

  const [createPrizeDraw, { isLoading: isCreating }] = useCreatePrizeDrawMutation();
  const [getSetupList] = useLazyGetSetupListQuery();

  useEffect(() => {
    if (isOpenModal) {
      form.resetFields();
      setFileSrc(null);
      setImageFile(null);
      setSelectedConfig(null);
      loadConfigurations();
      wheelRef.current?.reset();
      setSpinPhase('idle');
      setSelectedSegment(null);
    }
  }, [isOpenModal]);

  const loadConfigurations = async () => {
    try {
      const campaignParam = campaignId ? `&campaignId=${campaignId}` : '';
      const res = await getSetupList({ queryString: `isPublished=true&isInDateRange=true&hasRaffleConfig=false&sort=startAt&sortDirection=asc&limit=100${campaignParam}` }).unwrap();
      setConfigurations(res.data || []);
    } catch (e) {
      console.error('Error loading configurations:', e);
    }
  };

  const handleConfigChange = (configId: number) => {
    const config = configurations.find(c => c.id === configId) || null;
    setSelectedConfig(config);
  };

  const totalWinners = selectedConfig?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

  const handleDemoSpin = () => {
    if (spinPhase === 'spinning') return;
    setSpinPhase('spinning');
    setSelectedSegment(null);
    wheelRef.current?.startSpin();
    setTimeout(() => {
      wheelRef.current?.stopSpin();
    }, 1500);
  };

  const handleSpinEnd = (selected: RouletteSegment) => {
    setSpinPhase('finished');
    setSelectedSegment(selected);
  };

  const openChooseFile = () => {
    fileRef.current?.click();
  };

  const handleFileChange =  async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error({ title: intl.formatMessage({ id: 'Image size should be less than 2MB' }) });
      return;
    }
    if (!VALID_IMAGE_FORMATS.includes(file.type)) {
      toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
      return;
    }
    const isValidImage = await validateImageBytes(file);
    if (!isValidImage) {
      toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setFileSrc(reader.result as string);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleFinish = async (values: ConfigFormData) => {
    if (!selectedConfig) return;

    const formData = new FormData();
    formData.append('code', selectedConfig.code || String(selectedConfig.id));
    formData.append('name', selectedConfig.name);
    formData.append('campaignId', String(campaignId || ''));
    formData.append('configId', String(selectedConfig.id));
    formData.append('isActive', 'true');
    formData.append('mainWinners', String(totalWinners));
    formData.append('backupWinners', String(values.alternatesCount || 0));
    formData.append('includePreviousWinners', values.includePreviousWinners ? 'true' : 'false');
    formData.append('status', 'READY');
    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      await createPrizeDraw(formData).unwrap();
      toast.success({ title: intl.formatMessage({ id: 'Prize draw is created successfully!' }) });

      onSaveSuccess();
      onClose();
      
    } catch (e: any) {
      console.error('Prize draw create error:', e);
      const defaultMsg = intl.formatMessage({ id: 'Creating prize draw is failure!' });
      toast.error({ title:  getApiErrorMessage(e, intl.locale, defaultMsg) });
    }
  };

  const selectClassName = isModal ? 'select_modal' : 'select_form';

  const renderRoulettePreview = (diameter: number) => (
    <div className="roulette_preview_embedded">
      <h4 className="preview_title"><FormattedMessage id="Roulette preview" /></h4>
      <div className="preview_canvas_area">
        {fileSrc && (
          <div className="event_image_banner">
            <Img src={fileSrc} alt="Event" />
          </div>
        )}
        <RouletteWheel
          ref={wheelRef}
          segments={DEMO_SEGMENTS}
          diameter={diameter}
          indicatorPosition="top"
          spinSpeed={spinSpeed}
          decelDuration={decelDuration}
          onSpinEnd={handleSpinEnd}
        />
        <div className="preview_controls">
          {spinPhase !== 'spinning' && (
            <Button className="spin_btn" onClick={handleDemoSpin}>
              <FormattedMessage id="Demo spin" />
            </Button>
          )}
          {spinPhase === 'spinning' && (
            <p className="spinning_text"><FormattedMessage id="Spinning..." /></p>
          )}
          {spinPhase === 'finished' && selectedSegment && (
            <p className="selected_segment_text">
              <FormattedMessage id="Selected segment" />: <strong>{selectedSegment.label}</strong>
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderFormContent = () => (
    <Form
      form={form}
      className={isModal ? 'basic_info_form' : 'config_form'}
      onFinish={handleFinish}
      initialValues={{ alternatesCount: 0, includePreviousWinners: false }}
    >
      {isModal ? (
        <div className="config_modal_layout">
          <div className="config_modal_left">
            <div className="config_input">
              <p className="title"><FormattedMessage id="Configuration" /></p>
              <Form.Item name="prizeConfigId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                <Select
                  className={selectClassName}
                  suffixIcon={<ArrowDropDown />}
                  placeholder={intl.formatMessage({ id: 'Select configuration' })}
                  dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                  onChange={handleConfigChange}
                >
                  {configurations.map(cfg => (
                    <Select.Option key={cfg.id} value={cfg.id}>{cfg.name} [{ dayjs.utc(cfg.startAt).format('DD/MM/YYYY') } - { dayjs.utc(cfg.endAt).format('DD/MM/YYYY') }]</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {selectedConfig && selectedConfig.items?.length > 0 && (
              <div className="config_input prizes_list_section">
                <p className="title"><FormattedMessage id="Prizes in this configuration" /></p>
                <div className="prizes_list">
                  {selectedConfig.items.map((item, idx) => (
                    <div key={idx} className="prize_item">
                      <span className="prize_name">{item.reward?.rewardType?.name} - {item.reward?.name || `Premio #${item.rewardId}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="config_input winners_row">
              <div className="winners_field">
                <p className="title"><FormattedMessage id="Main winners" /></p>
                <Input type="number" value={totalWinners} disabled />
                <p className="field_hint"><FormattedMessage id="Main winners hint" /></p>
              </div>
              <div className="winners_field">
                <p className="title"><FormattedMessage id="Alternate winners" /></p>
                <Form.Item name="alternatesCount">
                  <Input type="number" min={0} max={50} placeholder={intl.formatMessage({ id: 'Alternate winners' })} />
                </Form.Item>
              </div>
            </div>

            <div className="config_input switch_row">
              <p className="title"><FormattedMessage id="Include previous winners" /></p>
              <Form.Item name="includePreviousWinners" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </div>

            <div className="config_input">
              <div className="photo_picker_wrapper">
                <div className="photo_info">
                  <p className="title"><FormattedMessage id="Event image" /></p>
                  <p className="description"><FormattedMessage id="Event image hint" /></p>
                  <Button className="select_photo_btn" onClick={openChooseFile}>
                    <FormattedMessage id="Select File" />
                  </Button>
                </div>
                <div className="photo_area" onClick={openChooseFile}>
                  {fileSrc && <Img src={fileSrc} alt="Roulette" />}
                </div>
                <input type="file" className="file_input" accept="image/*" ref={fileRef} onChange={handleFileChange} hidden />
              </div>
            </div>

            <div className="side_btns_camp">
              <div className="btn_wrapper">
                <Button className="save_sar_btn" htmlType="submit" loading={isCreating}>
                  <FormattedMessage id="Save" />
                  <ArrowForward />
                </Button>
              </div>
            </div>
          </div>

          <div className="config_modal_right">
            {renderRoulettePreview(280)}
          </div>
        </div>
      ) : (
        <div className="config_fullscreen_layout">
          <div className="config_fullscreen_left">
            <div className="config_form_fields">
              <div className="config_form_row">
                <div className="config_field">
                  <p className="title"><FormattedMessage id="Configuration" /></p>
                  <Form.Item name="prizeConfigId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                    <Select
                      className={selectClassName}
                      suffixIcon={<ArrowDropDown />}
                      placeholder={intl.formatMessage({ id: 'Select configuration' })}
                      dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                      onChange={handleConfigChange}
                    >
                      {configurations.map(cfg => (
                        <Select.Option key={cfg.id} value={cfg.id}>{cfg.name} [{ dayjs.utc(cfg.startAt).format('DD/MM/YYYY') } - { dayjs.utc(cfg.endAt).format('DD/MM/YYYY') }]</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>

              {selectedConfig && selectedConfig.items?.length > 0 && (
                <div className="config_form_row">
                  <div className="config_field prizes_list_section">
                    <p className="title"><FormattedMessage id="Prizes in this configuration" /></p>
                    <div className="prizes_list">
                      {selectedConfig.items.map((item, idx) => (
                        <div key={idx} className="prize_item">
                          <span className="prize_name">{item.reward?.name || `Premio #${item.rewardId}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="config_form_row two_columns">
                <div className="config_field">
                  <p className="title"><FormattedMessage id="Main winners" /></p>
                  <Input type="number" value={totalWinners} disabled />
                  <p className="field_hint"><FormattedMessage id="Main winners hint" /></p>
                </div>
                <div className="config_field">
                  <p className="title"><FormattedMessage id="Alternate winners" /></p>
                  <Form.Item name="alternatesCount">
                    <Input type="number" min={0} max={50} placeholder={intl.formatMessage({ id: 'Alternate winners' })} />
                  </Form.Item>
                </div>
              </div>
              <div className="config_form_row">
                <div className="config_field status_field">
                  <p className="title"><FormattedMessage id="Include previous winners" /></p>
                  <Form.Item name="includePreviousWinners" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                </div>
              </div>
              <div className="config_form_row">
                <div className="config_field">
                  <div className="photo_picker_wrapper">
                    <div className="photo_info">
                      <p className="title"><FormattedMessage id="Event image" /></p>
                      <p className="description"><FormattedMessage id="Event image hint" /></p>
                      <Button className="select_photo_btn" onClick={openChooseFile}>
                        <FormattedMessage id="Select File" />
                      </Button>
                    </div>
                    <div className="photo_area" onClick={openChooseFile}>
                      {fileSrc && <Img src={fileSrc} alt="Roulette" />}
                    </div>
                    <input type="file" className="file_input" accept="image/*" ref={fileRef} onChange={handleFileChange} hidden />
                  </div>
                </div>
              </div>
            </div>
            <div className="config_form_actions">
              <Button className="save_btn" htmlType="submit" loading={isCreating}>
                <FormattedMessage id="Save" />
                <ArrowForward />
              </Button>
              <Button className="cancel_btn" onClick={onClose}>
                <FormattedMessage id="Cancel" />
              </Button>
            </div>
          </div>

          <div className="config_fullscreen_right">
            {renderRoulettePreview(380)}
          </div>
        </div>
      )}
    </Form>
  );

  if (isModal) {
    return (
      <ModalDlg className="prizeDraws_config_modal" visible={isOpenModal ?? false} width={860} handleCancel={onClose} isCenter>
        <div className="modal_header">
          <div className="name_wrapper">
            <h3><FormattedMessage id="Configure prize draw" /></h3>
            <div className="close_modal" onClick={onClose}><Close /></div>
          </div>
        </div>
        <div className="new_config_form_wrapper">
          {renderFormContent()}
        </div>
      </ModalDlg>
    );
  }

  if (!isOpenModal) return null;

  return (
    <div className="config_form_fullscreen">
      <div className="config_form_wrapper">
        <div className="config_form_header">
          <h3><FormattedMessage id="Configure prize draw" /></h3>
        </div>
        {renderFormContent()}
      </div>
    </div>
  );
};

export default PrizeDrawsConfigModal;
